import React from "react";
import styled from "styled-components";

export function MaybeTooltipWrapper(props:{
    children:React.ReactNode,
    tooltip?:React.ReactNode,
}): JSX.Element
{
    if(props.tooltip)
    {
        return (
            <TooltipTheOneWrapper tooltipContent={props.tooltip} divWrapper>
                {props.children}
            </TooltipTheOneWrapper>
        );
    }
    else {
        return <>{props.children}</>;
    }
}

type TooltipTheOneWrapperProps = {
    children: React.ReactNode,
    tooltipContent?: React.ReactNode,
    divWrapper?: boolean,
    wrapperStyle?: React.CSSProperties,
    containerStyle?: React.CSSProperties,
    horizontalAlignment?: 'left' | 'right',
    verticalAlignment?: 'top' | 'bottom',
    display?:string,
    flex?:string,
    style?: React.CSSProperties,
    positioning?: 'absolute' | 'fixed', // Use fixed for troublesome positioning scenarios
};
export function TooltipTheOneWrapper (props: TooltipTheOneWrapperProps): JSX.Element
{
    const [showTooltip, setShowTooltip] = React.useState(false);
    const wrapperRef = React.useRef<HTMLElement | null>(null);

    const updateTooltipPosition = React.useCallback((): React.CSSProperties => {
        const bounds = wrapperRef.current?.getBoundingClientRect();
        if (!bounds)
            return {};

        const isAbove = props.verticalAlignment === 'top';
        const isLeftAligned = props.horizontalAlignment === 'left';

        return {
            position: 'fixed',
            top: isAbove ? 'auto' : bounds.bottom,
            bottom: isAbove ? window.innerHeight - bounds.top : 'auto',
            left: isLeftAligned ? bounds.left : 'auto',
            right: isLeftAligned ? 'auto' : window.innerWidth - bounds.right,
        };
    }, [props.horizontalAlignment, props.verticalAlignment]);

    const [tooltipPosition, setTooltipPosition] = React.useState<React.CSSProperties>({});

    React.useEffect(() => {
        if (!showTooltip || props.positioning !== 'fixed')
            return;

        const handlePositionChange = () => setTooltipPosition(updateTooltipPosition());
        window.addEventListener('scroll', handlePositionChange, true);
        window.addEventListener('resize', handlePositionChange);

        return () => {
            window.removeEventListener('scroll', handlePositionChange, true);
            window.removeEventListener('resize', handlePositionChange);
        };
    }, [props.positioning, showTooltip, updateTooltipPosition]);

    const handleMouseEnter = () => {
        if (props.positioning === 'fixed')
            setTooltipPosition(updateTooltipPosition());
        setShowTooltip(true);
    };

    const wrappedContent =
        <>
            {props.children}
            {showTooltip && props.tooltipContent && 
                <TooltipTheOneContainer
                    style={{
                        ...props.containerStyle,
                        ...(props.positioning === 'fixed' ? tooltipPosition : {}),
                    }}
                    horizontalAlignment={props.horizontalAlignment || 'right'}
                    verticalAlignment={props.verticalAlignment}>
                    {props.tooltipContent}
                </TooltipTheOneContainer>
            }
        </>;

    const wrapperProps = {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: () => setShowTooltip(false),
        onFocus: handleMouseEnter,
        onBlur: () => setShowTooltip(false),
        ref: (element: HTMLElement | null) => { wrapperRef.current = element; },
    };

    const wrapperStyle = {
        position: 'relative' as const,
        flex: props.flex || undefined,
        display: props.display || undefined,
        ...props.wrapperStyle
    };

    return (

        <>
            {props.divWrapper
                ?
                <div {...wrapperProps} style={wrapperStyle}>
                    {wrappedContent}
                </div>
                :
                <span {...wrapperProps} style={wrapperStyle}>
                    {wrappedContent}
                </span>
            }
        </>
    );
}

const Gray_dark = "rgba(61,61,61,1)";

const TooltipTheOneContainer = styled.span<{ horizontalAlignment: 'left' | 'right', verticalAlignment?: 'top' | 'bottom' }>`
    position: absolute;
    z-index: 99999999;
    top:100%;

    ${props => props.horizontalAlignment === 'left' ? 'left:0;' : 'right:0;'}
    ${props => props.verticalAlignment === 'top' ? 'bottom:100%; top:auto;' : ''}
    
    width: max-content;
    max-width: 400px;
    padding: 10px;
    transition: opacity 0.3s;
    
    background-color: ${Gray_dark};
    border-width: 1px;
    border-radius: 4px;
    border-style: solid;
    border-color: #555 transparent transparent transparent;
    font-family: "Noto Sans", sans-serif;
    font-size: 14px;
    color:#fff;
    font-weight: 400;

    p {
        color: #fff;
    }
`;